/**
 * Auth context — wraps the app and exposes the current session user
 * (or null) plus login/logout helpers.
 *
 * Calls /api/v1/auth/me on mount to detect existing sessions. The
 * sessions live server-side; the client just observes them via the
 * /me endpoint.
 *
 * Lesson 45: clearCsrfToken() is called on BOTH login AND logout
 * because csrf-csrf v4 binds the token to the session id.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, clearCsrfToken } from "./api";

export type Role = "OPERATOR" | "SUPERVISOR" | "ADMIN" | "READ_ONLY";

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  role: Role;
  languagePref: string;
}

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
  });

  const refresh = useCallback(async () => {
    try {
      const data = await api<{ user: SessionUser }>("/auth/me");
      setState({ user: data.user, loading: false, error: null });
    } catch {
      setState({ user: null, loading: false, error: null });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      // Lesson 45: clear cached csrf BEFORE login so the token is fetched
      // against the new session id.
      clearCsrfToken();
      const data = await api<{ user: SessionUser }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      // Clear AGAIN after login so the next mutating request fetches a
      // fresh token bound to the post-login session id.
      clearCsrfToken();
      setState({ user: data.user, loading: false, error: null });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "login_failed";
      setState({ user: null, loading: false, error: msg });
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api<{ ok: true }>("/auth/logout", { method: "POST" });
    } catch {
      /* ignore — logout is best-effort */
    }
    clearCsrfToken();
    setState({ user: null, loading: false, error: null });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}
